/**
 * Twilio SMS Integration
 * This module provides functionality to send SMS messages via Twilio API
 */
import supabase from './supabase';
import { smsService } from '../services/database';

// Twilio credentials from environment variables
const TWILIO_ACCOUNT_SID = import.meta.env.VITE_TWILIO_ACCOUNT_SID;
const TWILIO_AUTH_TOKEN = import.meta.env.VITE_TWILIO_AUTH_TOKEN;
const TWILIO_PHONE_NUMBER = import.meta.env.VITE_TWILIO_PHONE_NUMBER;

/**
 * Send SMS message via Twilio API
 * 
 * @param {string} to - Recipient's phone number (E.164 format)
 * @param {string} body - Message content
 * @param {string} customerId - Optional customer ID
 * @param {string} leadId - Optional lead ID
 * @returns {Promise<Object>} - Response with message details or error
 */
export const sendSMS = async (to, body, customerId = null, leadId = null) => {
  try {
    // Check if credentials are available
    if (!TWILIO_ACCOUNT_SID || !TWILIO_AUTH_TOKEN || !TWILIO_PHONE_NUMBER) {
      throw new Error('Twilio credentials are missing');
    }

    // Ensure phone number is in E.164 format
    const formattedNumber = formatPhoneNumber(to);
    
    // Make request to Twilio API
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${TWILIO_ACCOUNT_SID}/Messages.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Basic ' + btoa(`${TWILIO_ACCOUNT_SID}:${TWILIO_AUTH_TOKEN}`)
      },
      body: new URLSearchParams({
        To: formattedNumber,
        From: TWILIO_PHONE_NUMBER,
        Body: body
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send SMS');
    }

    const result = await response.json();
    
    // Save message to database
    await smsService.create({
      to: formattedNumber,
      from: TWILIO_PHONE_NUMBER,
      body,
      status: result.status,
      sid: result.sid,
      customerId,
      leadId
    });
    
    return {
      success: true,
      messageId: result.sid,
      status: result.status,
      message: 'SMS sent successfully'
    };
  } catch (error) {
    console.error('Error sending SMS:', error);
    return {
      success: false,
      error: error.message || 'Failed to send SMS'
    };
  }
};

/**
 * Format phone number to E.164 format
 * 
 * @param {string} phoneNumber - Input phone number
 * @returns {string} - E.164 formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Check if number already has country code
  if (digitsOnly.startsWith('1') && digitsOnly.length === 11) {
    return `+${digitsOnly}`;
  }
  
  // Add US country code if needed
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }
  
  // If number already has + prefix, return as is
  if (phoneNumber.startsWith('+')) {
    return phoneNumber;
  }
  
  // Default case: add + prefix to whatever we have
  return `+${digitsOnly}`;
};

/**
 * Get message status from Twilio API
 * 
 * @param {string} messageSid - Twilio Message SID
 * @returns {Promise<Object>} - Message status details
 */
export const getMessageStatus = async (messageSid) => {
  try {
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${TWILIO_ACCOUNT_SID}/Messages/${messageSid}.json`, {
      headers: {
        'Authorization': 'Basic ' + btoa(`${TWILIO_ACCOUNT_SID}:${TWILIO_AUTH_TOKEN}`)
      }
    });

    if (!response.ok) {
      throw new Error('Failed to retrieve message status');
    }

    const result = await response.json();
    
    // Update message status in database
    await smsService.updateStatus(messageSid, result.status);
    
    return {
      success: true,
      status: result.status,
      dateCreated: result.date_created,
      dateUpdated: result.date_updated
    };
  } catch (error) {
    console.error('Error getting message status:', error);
    return {
      success: false,
      error: error.message || 'Failed to retrieve message status'
    };
  }
};